import {
  selectAppSmartBanner,
  selectIsDisableAppBanner,
} from "@store/client/clientReducer";
import { useAppSelector } from "@store/store";
import { ExternalIcon } from "@icons/ExternalIcon";
import { CookieKeys } from "@lib/cookies";
import { useCookie } from "@hooks/useCookie/useCookie";
import { coerceBoolean } from "@lib/coerceBoolean";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getIsInIframe } from "@lib/iframe";
import { getMobilePlatform } from "@lib/getMobilePlatform";

import "./AppSmartBanner.css";

const getIsShouldShow = (
  platform: "ios" | "android" | undefined,
  isAppBannerDismissed: boolean | undefined,
  isShowOnIOS: boolean,
  isShowOnAndroid: boolean,
  isInIframe: boolean,
  isDisableAppBanner: boolean,
) => {
  if (
    isAppBannerDismissed === true ||
    isAppBannerDismissed === undefined ||
    isInIframe ||
    isDisableAppBanner
  ) {
    return false;
  }

  if (platform === "ios" && isShowOnIOS) {
    return true;
  }

  if (platform === "android" && isShowOnAndroid) {
    return true;
  }

  return false;
};

const getAppUrl = (
  platform: "ios" | "android" | undefined,
  appId: string,
  googlePlayId: string,
) => {
  if (platform === "ios") {
    return `https://itunes.apple.com/us/app/id${appId}`;
  }

  if (platform === "android") {
    return `https://play.google.com/store/apps/details?id=${googlePlayId}`;
  }

  return "";
};

const getAppCalloutText = (platform: "ios" | "android" | undefined) => {
  // legacy is not translated, keeping existing behavior for that.
  if (platform === "ios") {
    return "On the App Store";
  }

  if (platform === "android") {
    return "In Google Play";
  }

  return "";
};

const platform = getMobilePlatform();
const appCalloutText = getAppCalloutText(platform);

const AppSmartBanner = () => {
  const { t } = useTranslation();
  const appSmartBanner = useAppSelector(selectAppSmartBanner);
  const [isAppBannerDismissed, setIsAppBannerDismissed] = useCookie(
    CookieKeys.appSmartBannerDismissed,
    false,
    coerceBoolean,
  );
  const isDisableAppBanner = useAppSelector(selectIsDisableAppBanner);

  const isInIframe = getIsInIframe();

  const isShouldShow = useMemo(() => {
    return getIsShouldShow(
      platform,
      isAppBannerDismissed,
      appSmartBanner.isShowOnIOS,
      appSmartBanner.isShowOnAndroid,
      isInIframe,
      isDisableAppBanner,
    );
  }, [
    isAppBannerDismissed,
    appSmartBanner.isShowOnIOS,
    appSmartBanner.isShowOnAndroid,
    isInIframe,
    isDisableAppBanner,
  ]);

  const appUrl = useMemo(() => {
    return getAppUrl(
      platform,
      appSmartBanner.appleAppId,
      appSmartBanner.googleAppId,
    );
  }, [appSmartBanner.appleAppId, appSmartBanner.googleAppId]);

  const handlePress = () => {
    // TODO: send analytics for `app_banner_open`
  };

  const handleClose = () => {
    setIsAppBannerDismissed(
      true,
      appSmartBanner.minutesToKeepClosed &&
        appSmartBanner.minutesToKeepClosed > 0
        ? appSmartBanner.minutesToKeepClosed / (60 * 24)
        : "session",
    );
  };

  if (!isShouldShow) {
    return null;
  }

  return (
    <div className="app-smart-banner-component">
      <div className="close-app-smart-banner">
        <button className="close-button" onClick={handleClose}>
          <ExternalIcon svgName="close" size={14} color="#767676" />
        </button>
      </div>
      {appSmartBanner.appIcon && (
        <div className="image">
          <img
            src={appSmartBanner.appIcon}
            alt={appSmartBanner.appTitle}
            className="image-icon"
          />
        </div>
      )}
      <div className="content">
        <div className="title">{appSmartBanner.appTitle}</div>
        <div className="author">Visit Widget LLC</div>
        <div className="price">
          <span className="price-text">{t("common.free")}</span> -{" "}
          {appCalloutText}
        </div>
      </div>
      <div className="action-button">
        <a href={appUrl} onClick={handlePress}>
          {t("common.view")}
        </a>
      </div>
    </div>
  );
};

export { AppSmartBanner };
